import React from 'react';

const quantum101 = {
    intro: {
        title: 'Quantum Computing 101',
        subTitle: 'A Practical Primer',
    },
    subIntro: {
        leftAligned: true,
        children: (
            <>
                <p>Everything you need to understand about quantum computing, quantum optimization, and post-quantum cryptography — explained clearly.</p>
                <p>Quantum computing has moved from physics labs to production systems. But the hype cycle has left most decision-makers confused about what's real, what's coming, and what matters for their organizations. This guide cuts through the noise with honest, practical explanations — no PhD required.</p>
            </>
        ),
    },
    divider: {},
    subIntro1: {
        leftAligned: true,
        children: (
            <>
                <h2>The Fundamentals: Bits vs Qubits</h2>
                <p>Classical computers process information using bits (0 or 1). Quantum computers use qubits, which exploit two phenomena that have no classical equivalent.</p>
            </>
        ),
    },
    cardsGroup: {
        children: [
            {
                icon: 'icon-layers',
                title: 'Superposition',
                card: "A qubit can be both 0 and 1 simultaneously until measured. 50 qubits = 2⁵⁰ states (over one quadrillion) explored in parallel. 300 qubits = more states than atoms in the observable universe. This exponential scaling is the source of quantum advantage.",
            },
            {
                icon: 'icon-link-2',
                title: 'Entanglement',
                card: "When qubits become entangled, measuring one instantly affects the others — regardless of distance. Einstein called it 'spooky action at a distance.' This lets quantum computers probe relationships between variables simultaneously, with perfect coordination.",
            },
            {
                icon: 'icon-alert-triangle',
                title: 'The Catch',
                card: "When you measure a quantum system, superposition collapses — you get one answer, not all answers. Quantum algorithms must be carefully designed so correct answers amplify and wrong answers cancel out. Quantum advantage is real but not universal.",
            },
        ],
    },
    divider1: {},
    subIntro2: {
        leftAligned: true,
        children: (
            <>
                <h2>Why Quantum Solves Optimization</h2>
                <p>The key insight: quantum systems naturally seek their lowest energy state (ground state). This is mathematically equivalent to finding optimal solutions.</p>
            </>
        ),
    },
    cardsGroup1: {
        children: [
            {
                icon: 'icon-trend-down',
                title: 'Ground State = Optimal Solution',
                card: "Encode your problem as energy: cost becomes energy to minimize, constraint violations become energy penalties. The quantum system physically evolves toward the lowest-energy state — nature does the optimization. No searching required.",
            },
            {
                icon: 'icon-lightning',
                title: 'Quantum Tunneling',
                card: "Classical algorithms get trapped in local optima — small valleys surrounded by hills. Quantum systems tunnel through barriers instead of climbing over them. This isn't a metaphor — it's real physics that lets quantum find global optima classical methods miss.",
            },
            {
                icon: 'icon-code',
                title: 'QUBO: The Universal Format',
                card: "Most optimization problems can be expressed as Quadratic Unconstrained Binary Optimization (QUBO), which maps directly to quantum hardware. Your scheduling problem = a magnetic system seeking equilibrium. Your routing problem = a spin glass finding its ground state.",
            },
        ],
    },
    divider2: {},
    subIntro3: {
        leftAligned: true,
        children: (
            <>
                <h2>Types of Quantum Computers</h2>
                <p>Not all quantum computers are the same. Different architectures suit different problems.</p>
            </>
        ),
    },
    cardsGroup2: {
        children: [
            {
                icon: 'icon-processor',
                title: 'Gate-Based Quantum',
                card: "Universal quantum computers using quantum logic gates. Most flexible but require extremely low error rates. Leading players: IBM, Google, IonQ. Current status: Useful for research, limited practical advantage today for most business problems.",
            },
            {
                icon: 'icon-lightning',
                title: 'Quantum Annealers',
                card: "Purpose-built for optimization. Find ground states of Ising Hamiltonians (QUBO problems). D-Wave offers 5000+ qubits today. Proven production results. The architecture FENNEQ uses for real-world optimization.",
            },
            {
                icon: 'icon-sun',
                title: 'Photonic Quantum',
                card: "Uses photons as qubits. Naturally resistant to decoherence. Leading players: Xanadu, PsiQuantum. Promising for sampling and simulation, earlier in commercialization.",
            },
            {
                icon: 'icon-atom',
                title: 'Trapped Ion / Neutral Atom',
                card: "Alternative qubit implementations with different tradeoffs in coherence and scalability. Leading players: Quantinuum, QuEra. Strong for high-fidelity small-scale computation.",
            },
        ],
    },
    divider3: {},
    subIntro4: {
        leftAligned: true,
        children: (
            <>
                <h2>The State of Quantum Computing: 2025</h2>
                <p>We're in the NISQ era: Noisy Intermediate-Scale Quantum. Current machines are error-prone with 50-5000+ qubits — useful but not yet fault-tolerant.</p>
            </>
        ),
    },
    cardsGroup3: {
        children: [
            {
                icon: 'icon-check-circle',
                title: 'What Works Today',
                card: "Quantum annealing for optimization (D-Wave, 5000+ qubits). Specific gate-based algorithms (VQE, QAOA). Quantum random number generation. Quantum key distribution. Post-quantum cryptography standards.",
            },
            {
                icon: 'icon-cross-circle',
                title: "What Doesn't Work Yet",
                card: "General-purpose quantum speedup. Breaking encryption (requires thousands of error-corrected qubits). General ML acceleration. Large-scale quantum simulation.",
            },
            {
                icon: 'icon-trend-up',
                title: 'The Inflection Point',
                card: "Hybrid quantum-classical systems have moved quantum from 'research curiosity' to 'production capability' for the right problems. Organizations exploring now will have significant advantages as hardware improves.",
            },
        ],
    },
    divider4: {},
    subIntro5: {
        leftAligned: true,
        children: (
            <>
                <h2>Hybrid Quantum-Classical: The Practical Approach</h2>
                <p>Pure quantum solutions are rare. The practical approach combines quantum and classical computing — each doing what it does best.</p>
            </>
        ),
    },
    cardsGroup4: {
        children: [
            {
                icon: 'icon-cloud-upload',
                title: '1. Data Ingestion',
                card: "Your historical data — demand patterns, resource constraints, past decisions — is processed classically. We extract features, identify patterns, and prepare the problem structure.",
            },
            {
                icon: 'icon-chart-line',
                title: '2. Predictive Modeling',
                card: "ML models predict uncertain quantities: future demand, equipment failures, customer behavior. Better predictions mean better optimization.",
            },
            {
                icon: 'icon-code',
                title: '3. Problem Formulation',
                card: "We translate your business problem into mathematical form — objectives, constraints, decision variables — then map to QUBO or other quantum-suitable formats.",
            },
            {
                icon: 'icon-layers',
                title: '4. Decomposition',
                card: "If your problem is too large for direct quantum processing, we decompose it into subproblems. This requires understanding which variables interact strongly.",
            },
            {
                icon: 'icon-processor',
                title: '5. Quantum Solving',
                card: "Prepared subproblems go to D-Wave quantum annealers. The quantum system explores solution space through superposition and tunnels through barriers.",
            },
            {
                icon: 'icon-sliders-horiz',
                title: '6. Classical Refinement',
                card: "Quantum solutions are refined classically: local search, constraint repair, feasibility verification. Quantum gets us to the right neighborhood; classical finds the best house.",
            },
        ],
    },
    divider5: {},
    subIntro6: {
        leftAligned: true,
        children: (
            <>
                <h2>The Cryptographic Threat — and Response</h2>
                <p>Quantum computing doesn't just solve optimization — it breaks encryption. Shor's algorithm will break RSA and ECC. The only question is when.</p>
            </>
        ),
    },
    cardsGroup5: {
        children: [
            {
                icon: 'icon-lock-opened',
                title: "Shor's Algorithm",
                card: "Factors large numbers exponentially faster than classical methods. RSA-2048 could be broken in hours. ECC keys would fall in minutes. This is mathematical proof, not speculation. Timeline estimates: 5-15 years.",
            },
            {
                icon: 'icon-clock',
                title: 'Harvest Now, Decrypt Later',
                card: "Adversaries are intercepting and storing encrypted data today, waiting for quantum computers to mature. Any data that must stay confidential for 10+ years is at risk right now. This is why you must act today.",
            },
            {
                icon: 'icon-search',
                title: "Grover's Algorithm",
                card: "Provides quadratic speedup for unstructured search. AES-128 drops to ~64-bit security (breakable). AES-256 drops to ~128-bit (still secure). Solution: double your symmetric key lengths.",
            },
            {
                icon: 'icon-shield-check',
                title: 'Post-Quantum Cryptography',
                card: "New algorithms resistant to both classical and quantum attacks. NIST standardized ML-KEM (FIPS 203) for key exchange and ML-DSA (FIPS 204) for signatures. Based on lattice problems that remain hard for quantum.",
            },
        ],
    },
    divider6: {},
    subIntro7: {
        leftAligned: true,
        children: (
            <>
                <h2>QASER: Sovereign Post-Quantum Cryptography</h2>
                <p>Qamia's homegrown ML-KEM and ML-DSA implementations. No foreign backdoors, no export restrictions, local expertise.</p>
            </>
        ),
    },
    cardsGroup6: {
        children: [
            {
                icon: 'icon-search',
                title: 'QamiaScanner',
                card: "Instant cryptographic discovery across your infrastructure. You can't migrate what you can't find. Know your exposure before attackers do.",
            },
            {
                icon: 'icon-shield-check',
                title: 'QamiaLayer',
                card: "Quantum-safe tunneling with zero code changes. Protect legacy systems in 4 weeks. Works with existing infrastructure immediately.",
            },
            {
                icon: 'icon-code',
                title: 'QamiaIntegrate',
                card: "Full ML-KEM and ML-DSA implementation for permanent migration. Native integration with professional services. Timeline: 6+ months.",
            },
            {
                icon: 'icon-layers',
                title: 'QamiaOrchestrator',
                card: "Ongoing crypto-agility management. Continuous compliance monitoring. Certificate lifecycle automation. Designed to swap algorithms as standards evolve.",
            },
        ],
    },
    divider7: {},
    subIntro8: {
        leftAligned: true,
        children: (
            <>
                <h2>What This Means for Your Organization</h2>
                <p>Quantum computing isn't a future concern. It's a present decision.</p>
            </>
        ),
    },
    cardsGroup7: {
        children: [
            {
                icon: 'icon-sliders-horiz',
                title: 'For Operations & Optimization',
                card: "If you face complex scheduling, routing, allocation, or planning problems with millions of configurations — quantum-hybrid approaches can help. Start with a proof of concept. Measure against current methods. Scale what works.",
            },
            {
                icon: 'icon-lock',
                title: 'For Security & Risk',
                card: "If you handle data with long-term confidentiality requirements (government, financial, healthcare, IP, legal), post-quantum migration is urgent. Run QamiaScanner immediately. Know your exposure. Prioritize by sensitivity.",
            },
            {
                icon: 'icon-compass',
                title: 'For Strategy & Planning',
                card: "Even if immediate deployment isn't right, understanding quantum's trajectory matters. Organizations building expertise now will have advantages. Those starting late will scramble — paying premium prices and accepting vendor lock-in.",
            },
        ],
    },
    divider8: {},
    subIntro9: {
        leftAligned: true,
        children: (
            <>
                <h2>Glossary</h2>
            </>
        ),
    },
    cardsGroup8: {
        children: [
            {
                icon: 'icon-info',
                title: 'Qubit',
                card: "Quantum bit. Fundamental unit of quantum information, capable of superposition (being 0 and 1 simultaneously).",
            },
            {
                icon: 'icon-info',
                title: 'Superposition',
                card: "Quantum state where a qubit exists in multiple states simultaneously until measured.",
            },
            {
                icon: 'icon-info',
                title: 'Entanglement',
                card: "Quantum correlation between qubits where measuring one instantly affects observations of others.",
            },
            {
                icon: 'icon-info',
                title: 'Ground State',
                card: "The lowest-energy configuration of a quantum system. Corresponds to optimal solutions in quantum optimization.",
            },
            {
                icon: 'icon-info',
                title: 'Hamiltonian',
                card: "Mathematical operator describing the total energy of a quantum system. Defines what ground state the system seeks.",
            },
            {
                icon: 'icon-info',
                title: 'QUBO',
                card: "Quadratic Unconstrained Binary Optimization. Standard format for expressing optimization problems on quantum hardware.",
            },
        ],
    },
    cardsGroup9: {
        children: [
            {
                icon: 'icon-info',
                title: 'Quantum Tunneling',
                card: "Phenomenon where particles pass through energy barriers. Enables escape from local optima in optimization.",
            },
            {
                icon: 'icon-info',
                title: 'NISQ',
                card: "Noisy Intermediate-Scale Quantum. Current era with 50-5000+ qubits but significant noise and limited error correction.",
            },
            {
                icon: 'icon-info',
                title: 'Quantum Annealing',
                card: "Method of finding ground states by slowly evolving a quantum system. Used by D-Wave machines for optimization.",
            },
            {
                icon: 'icon-info',
                title: "Shor's Algorithm",
                card: "Quantum algorithm for factoring large numbers exponentially faster than classical methods. Breaks RSA and ECC encryption.",
            },
            {
                icon: 'icon-info',
                title: 'ML-KEM / ML-DSA',
                card: "NIST-standardized post-quantum algorithms for key exchange (FIPS 203) and digital signatures (FIPS 204).",
            },
            {
                icon: 'icon-info',
                title: 'Crypto-Agility',
                card: "Designing systems to easily swap cryptographic algorithms as standards evolve or vulnerabilities emerge.",
            },
        ],
    },
    divider9: {},
    quote: {
        children: (
            <>
                Ready to explore what quantum can do for your organization?
            </>
        ),
        cta: {
            url: '/contact',
            children: 'Talk to Our Team',
        },
    },
};

export default quantum101;
